import type { CompanyDiscoveryAiOutput } from '@revelio/data-access';

import { getFilterDefinition } from '../../engine/filterRegistry';
import type {
  FreeTextFilter,
  ListFilter,
  RangeFilter,
  SchoolFilter,
  StaticListFilter,
  TreeFilter,
} from '../../types/filters';
import { FilterId, FilterType } from '../../types/filters';

type CompanyInfoSelectors =
  CompanyDiscoveryAiOutput['filters']['entity_info_selectors']['company_info_selectors'];

type ResolvedPromptItem = {
  name: string;
  relevantPromptText: string;
  id?: string;
  levelId?: string;
  rcid?: string;
  rsid?: string;
};

type RangeItem = {
  start_value: number | null;
  end_value: number | null;
  relevantPromptText: string;
};

/**
 * Converts CompanyDiscoveryAiOutput entity_info_selectors.company_info_selectors
 * (with backend-resolved id/levelId/rcid/rsid) into CustomDash Filter[].
 */
export const transformEntityInfo = (
  companyInfoSelectors: CompanyInfoSelectors | undefined | null
): (
  | TreeFilter
  | RangeFilter
  | StaticListFilter
  | ListFilter
  | FreeTextFilter
  | SchoolFilter
)[] => {
  const filters: (
    | TreeFilter
    | RangeFilter
    | StaticListFilter
    | ListFilter
    | FreeTextFilter
    | SchoolFilter
  )[] = [];
  if (!companyInfoSelectors || typeof companyInfoSelectors !== 'object') {
    return filters;
  }

  const c = companyInfoSelectors;

  if (Array.isArray(c.keywords) && c.keywords.length > 0) {
    const def = getFilterDefinition(FilterId.KEYWORDS);
    if (def?.type === FilterType.FREE_TEXT) {
      filters.push({
        ...def,
        id: FilterId.KEYWORDS,
        type: FilterType.FREE_TEXT,
        text: c.keywords.map((t) =>
          (typeof t === 'string' ? t : String(t)).toLowerCase()
        ),
        isUserSubmitted: true,
      } as FreeTextFilter);
    }
  }

  if (Array.isArray(c.competitors_of) && c.competitors_of.length > 0) {
    const def = getFilterDefinition(FilterId.COMPETITORS);
    if (def?.type === FilterType.TREE) {
      const selectionListIds = (def as TreeFilter).selectionListIds ?? [];
      const selections: TreeFilter['selections'] = (
        c.competitors_of as ResolvedPromptItem[]
      )
        .filter((item) => item.rcid != null)
        .map((item) => ({
          id: String(item.rcid),
          name: item.name,
          levelId: 'company' as TreeFilter['selections'][0]['levelId'],
        }));
      if (selections.length > 0) {
        filters.push({
          ...def,
          id: FilterId.COMPETITORS,
          type: FilterType.TREE,
          selectionListIds,
          selections,
          isUserSubmitted: true,
        } as TreeFilter);
      }
    }
  }

  if (c.is_subsidiary === true || c.is_subsidiary === false) {
    const def = getFilterDefinition(FilterId.EXTERNALLY_OWNED);
    if (def?.type === FilterType.STATIC_LIST) {
      const id = c.is_subsidiary ? 'true' : 'false';
      filters.push({
        ...def,
        id: FilterId.EXTERNALLY_OWNED,
        type: FilterType.STATIC_LIST,
        selections: [{ id, name: c.is_subsidiary ? 'True' : 'False' }],
        isUserSubmitted: true,
      } as StaticListFilter);
    }
  }

  if (c.is_public === true || c.is_public === false) {
    const def = getFilterDefinition(FilterId.PUBLIC);
    if (def?.type === FilterType.STATIC_LIST) {
      filters.push({
        ...def,
        id: FilterId.PUBLIC,
        type: FilterType.STATIC_LIST,
        selections: [
          {
            id: c.is_public ? 'true' : 'false',
            name: c.is_public ? 'True' : 'False',
          },
        ],
        isUserSubmitted: true,
      } as StaticListFilter);
    }
  }

  if (Array.isArray(c.hq_geography) && c.hq_geography.length > 0) {
    const def = getFilterDefinition(FilterId.HQ_LOCATION);
    if (def?.type === FilterType.TREE) {
      const selectionListIds = (def as TreeFilter).selectionListIds ?? [];
      const selections: TreeFilter['selections'] = (
        c.hq_geography as ResolvedPromptItem[]
      )
        .filter((item) => item.id != null && item.levelId != null)
        .map((item) => ({
          id: item.id as string,
          name: item.name,
          levelId: item.levelId as TreeFilter['selections'][0]['levelId'],
        }));
      if (selections.length > 0) {
        filters.push({
          ...def,
          id: FilterId.HQ_LOCATION,
          type: FilterType.TREE,
          selectionListIds,
          selections,
          isUserSubmitted: true,
        } as TreeFilter);
      }
    }
  }

  if (Array.isArray(c.founder_ethnicity) && c.founder_ethnicity.length > 0) {
    const def = getFilterDefinition(FilterId.FOUNDER_ETHNICITY);
    if (def?.type === FilterType.LIST) {
      const selectionListId = (def as ListFilter).selectionListId;
      const selections = (c.founder_ethnicity as ResolvedPromptItem[])
        .filter((item) => item.id != null)
        .map((item) => ({ id: item.id as string, name: item.name }));
      if (selections.length > 0) {
        filters.push({
          ...def,
          id: FilterId.FOUNDER_ETHNICITY,
          type: FilterType.LIST,
          selectionListId,
          selections,
          isUserSubmitted: true,
        } as ListFilter);
      }
    }
  }

  if (Array.isArray(c.founder_gender) && c.founder_gender.length > 0) {
    const def = getFilterDefinition(FilterId.FOUNDER_GENDER);
    if (def?.type === FilterType.LIST) {
      const selectionListId = (def as ListFilter).selectionListId;
      const selections = (c.founder_gender as ResolvedPromptItem[])
        .filter((item) => item.id != null)
        .map((item) => ({ id: item.id as string, name: item.name }));
      if (selections.length > 0) {
        filters.push({
          ...def,
          id: FilterId.FOUNDER_GENDER,
          type: FilterType.LIST,
          selectionListId,
          selections,
          isUserSubmitted: true,
        } as ListFilter);
      }
    }
  }

  if (
    Array.isArray(c.founder_prev_company) &&
    c.founder_prev_company.length > 0
  ) {
    const def = getFilterDefinition(FilterId.FOUNDER_COMPANY);
    if (def?.type === FilterType.TREE) {
      const selectionListIds = (def as TreeFilter).selectionListIds ?? [];
      const selections: TreeFilter['selections'] = (
        c.founder_prev_company as ResolvedPromptItem[]
      )
        .filter((item) => item.rcid != null)
        .map((item) => ({
          id: String(item.rcid),
          name: item.name,
          levelId: 'company' as TreeFilter['selections'][0]['levelId'],
        }));
      if (selections.length > 0) {
        filters.push({
          ...def,
          id: FilterId.FOUNDER_COMPANY,
          type: FilterType.TREE,
          selectionListIds,
          selections,
          isUserSubmitted: true,
        } as TreeFilter);
      }
    }
  }

  if (
    Array.isArray(c.founder_prev_university) &&
    c.founder_prev_university.length > 0
  ) {
    const def = getFilterDefinition(FilterId.FOUNDER_PREVIOUS_SCHOOL);
    if (def?.type === FilterType.SCHOOL) {
      const schoolResultItems: Record<
        string,
        { label: string; name: string; primary_name: string; rsid: string }
      > = {};
      for (const item of c.founder_prev_university as ResolvedPromptItem[]) {
        const rsid = item.rsid ?? item.name;
        const key = rsid;
        schoolResultItems[key] = {
          label: item.name,
          name: item.name,
          primary_name: item.name,
          rsid,
        };
      }
      filters.push({
        ...def,
        id: FilterId.FOUNDER_PREVIOUS_SCHOOL,
        type: FilterType.SCHOOL,
        schoolResultItems,
        isUserSubmitted: true,
      } as SchoolFilter);
    }
  }

  if (Array.isArray(c.funding_stage) && c.funding_stage.length > 0) {
    const def = getFilterDefinition(FilterId.FUNDING_STAGE);
    if (def?.type === FilterType.STATIC_LIST) {
      const items = c.funding_stage as ResolvedPromptItem[];
      const selections = items
        .filter((item) => item.id != null)
        .map((item) => ({ id: item.id as string, name: item.name }));
      if (selections.length > 0) {
        filters.push({
          ...def,
          id: FilterId.FUNDING_STAGE,
          type: FilterType.STATIC_LIST,
          selections,
          isUserSubmitted: true,
        } as StaticListFilter);
      }
    }
  }

  const rangeMappings: Array<{
    key: keyof CompanyInfoSelectors;
    filterId: FilterId;
  }> = [
    { key: 'last_funding_amount', filterId: FilterId.LAST_FUNDING_AMOUNT },
    { key: 'last_funding_year', filterId: FilterId.LAST_FUNDING_YEAR },
    { key: 'total_funding_amount', filterId: FilterId.TOTAL_FUNDING_AMOUNT },
    { key: 'year_founded', filterId: FilterId.YEAR_FOUNDED },
    { key: 'valuation', filterId: FilterId.VALUATION },
  ];

  for (const { key, filterId } of rangeMappings) {
    const ranges = c[key];
    if (!Array.isArray(ranges) || ranges.length === 0) continue;
    const r = ranges[0] as RangeItem | undefined;
    if (!r || (r.start_value === null && r.end_value === null)) continue;
    const min = r.start_value !== null ? r.start_value : undefined;
    const max = r.end_value !== null ? r.end_value : undefined;
    const def = getFilterDefinition(filterId);
    if (def?.type !== FilterType.RANGE) continue;
    filters.push({
      ...def,
      id: filterId,
      type: FilterType.RANGE,
      min,
      max,
      isUserSubmitted: true,
    } as RangeFilter);
  }

  return filters;
};
